import { format } from "date-fns";

import type { DealStage } from "../types";

export const findDealLabel = (dealStages: DealStage[], dealValue: string) => {
  if (!dealValue) return "";
  const dealStage =
    dealStages?.find((stage) => stage.value === dealValue) ||
    dealStages?.find(
      (stage) => stage.value.toLowerCase() === dealValue.toLowerCase(),
    );
  return dealStage?.label || dealValue;
};

export function getRelativeTimeString(
  dateString: string,
  locale = "en",
): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  date.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = date.getTime() - today.getTime();
  const unitDiff = Math.round(diff / (1000 * 60 * 60 * 24));

  if (!Number.isFinite(unitDiff)) {
    return date.toLocaleDateString(locale);
  }

  // Check if the date is more than one week old
  if (Math.abs(unitDiff) > 7) {
    try {
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
      }).format(date);
    } catch {
      return date.toLocaleDateString(locale);
    }
  }

  // Intl.RelativeTimeFormat for dates within the last week
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    return ucFirst(rtf.format(unitDiff, "day"));
  } catch {
    return date.toLocaleDateString(locale);
  }
}

function ucFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const isoDateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

export function formatISODateString(dateString: string) {
  if (!isoDateStringRegex.test(dateString)) {
    throw new Error("Invalid date format. Expected YYYY-MM-DD.");
  }
  // Some browsers will consider a date in the format YYYY-MM-DD as UTC, which can cause off-by-one-day issues depending on the user's timezone.
  // To avoid this, we can parse the date components manually and create a date object in the local timezone.
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return format(date, "PP");
}
